

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tbody, Td, Th, Thead, Tr } from "react-super-responsive-table";
import { FaCheck } from "react-icons/fa";
import { FiEdit2 } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../../../services/formatDate";
import ConfirmationModal from "../../../Common/ConfirmationModal";
import { deleteNotes, fetchInstructorNotes } from "../../../../services/operations/notesAPI";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import * as pdfjsLib from "pdfjs-dist/webpack";


export default function NotesTable({ notes, setNotes }) {
  const navigate = useNavigate();
  const { token } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(null);
  const TRUNCATE_LENGTH = 30;

  // Track PDF previews for each note
  const [pdfPreviews, setPdfPreviews] = useState({});

  // Function to delete notes
  const handleNotesDelete = async (notesId) => {
    setLoading(true);
    await deleteNotes({ notesId }, token);
    const result = await fetchInstructorNotes(token);
    if (result) {
      setNotes(result);
    }
    setConfirmationModal(null);
    setLoading(false);
  };

  // Function to handle PDF preview generation
  const handlePdfPreview = async (pdfUrl, noteId) => {
    try {
      // Fetch the PDF file
      const response = await fetch(pdfUrl);
      const pdfData = await response.arrayBuffer();
      const pdf = await pdfjsLib.getDocument(pdfData).promise;
      const page = await pdf.getPage(1);

      const scale = 1.5;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      // Update the preview state for the specific note
      setPdfPreviews((prev) => ({
        ...prev,
        [noteId]: canvas.toDataURL(), // Store the preview by noteId
      }));
    } catch (error) {
      console.error("Error generating PDF preview:", error.message);
    }
  };
  
  
  // Generate previews for all notes with PDFs
  useEffect(() => {
    if (notes?.length > 0) {
      notes.forEach((note) => {
        if (note.NotesPdf && note.NotesPdf.includes('pdf')) {
          handlePdfPreview(note.NotesPdf, note._id); // Pass the note ID to associate with the preview
        }
      });
    }
  }, [notes]);

  return (
    <>
      <Table className="rounded-xl text-white bg-pure-greys-150">
        <Thead>
          <Tr className="flex gap-x-10 rounded-t-md border-b border-b-white px-6 py-2">
            <Th className="flex-1 text-left text-sm font-medium text-semibold uppercase text-richblack-900">
              Notes
            </Th>
            <Th className="text-left text-sm font-medium uppercase text-richblack-900 text-semibold">
              Actions
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {notes?.length === 0 ? (
            <Tr>
              <Td className="py-10 text-center text-2xl font-medium text-richblack-800">
                No Notes found🙂
              </Td>
            </Tr>
          ) : (
            notes.map((note) => (
              <Tr
                key={note._id}
                className="flex gap-x-10 border-b border-white px-6 py-8"
              >
                <Td className="flex flex-1 gap-x-4">
                  {pdfPreviews[note._id] ? (
                    <img
                      src={pdfPreviews[note._id]} // Display the specific preview
                      alt={note.NotesName}
                      className="h-[148px] w-[220px] rounded-lg object-cover"
                    />
                  ) : (
                    <div>Loading Preview...</div>
                  )}
                  <div className="flex flex-col justify-between">
                    <p className="text-lg font-semibold uppercase text-richblack-5">
                      {note.NotesName}
                    </p>
                    <p className="text-sm text-richblack-600 font-bold">
                      category:
                      <span className="text-xs text-richblack-400 text-semibold"> {note?.category?.name}</span>
                    </p>
                    <p className="text-sm text-richblack-600 lowercase font-bold">
                      Description:
                      <span className="text-xs text-richblack-400 text-semibold"> {note.NotesDescription.substr(0, TRUNCATE_LENGTH)}...</span>
                    </p>
                    <p className="text-sm text-richblack-600 font-bold">
                      created:
                      <span className="text-xs text-richblack-400 text-semibold"> {formatDate(note.createdAt)}</span>
                    </p>
                    <p className="flex w-fit flex-row items-center gap-2 rounded-full bg-richblack-700 px-2 py-[2px] text-[12px] font-medium text-blue-100">
                      <div className="flex h-3 w-3 items-center justify-center rounded-full bg-blue-100 text-richblack-700">
                        <FaCheck size={8} />
                      </div>
                      Published
                    </p>
                  </div>
                </Td>
                <Td className="text-sm font-medium text-richblack-100">
                  <button
                    disabled={loading}
                    onClick={() => navigate(`/dashboard/edit-notes/${note._id}`)}
                    title="Edit"
                    className=" text-caribbeangreen-700 px-2 transition-all duration-200 hover:scale-110 hover:text-caribbeangreen-300"
                  >
                    <FiEdit2 size={20} />
                  </button>
                  <button
                    disabled={loading}
                    onClick={() =>
                      setConfirmationModal({
                        text1: "Do you want to delete this note?",
                        text2: "All data related to this note will be deleted",
                        btn1Text: !loading ? "Delete" : "Loading...",
                        btn2Text: "Cancel",
                        btn1Handler: !loading
                          ? () => handleNotesDelete(note._id)
                          : () => {},
                        btn2Handler: !loading ? () => setConfirmationModal(null) : () => {},
                      })
                    }
                    title="Delete"
                    className=" text-pink-150 px-1 transition-all duration-200 hover:scale-110 hover:text-[#ff0000]"
                  >
                    <RiDeleteBin6Line size={20} />
                  </button>
                </Td>
              </Tr>
            ))
          )}
        </Tbody>
      </Table>

      {confirmationModal && <ConfirmationModal modalData={confirmationModal} />}
    </>
  );
}
